/**
 * S4U Coverflow Slider
 * - Loads Swiper (CSS + JS) from CDN once
 * - Initializes all `[data-s4u-coverflow]` sections
 */

const SWIPER_JS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
const SWIPER_CSS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';

/** @type {Map<string, any>} */
const instances = new Map();

function prefersReducedMotion() {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
}

/**
 * @returns {Promise<void>}
 */
async function ensureSwiper() {
  const SwiperGlobal = /** @type {any} */ (window).Swiper;
  if (SwiperGlobal) return;

  if (!document.querySelector('link[data-s4u-swiper]')) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = SWIPER_CSS;
    link.setAttribute('data-s4u-swiper', '');
    document.head.appendChild(link);
  }

  await new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-s4u-swiper]');
    if (existing) {
      if ((/** @type {any} */ (window)).Swiper) resolve(undefined);
      else existing.addEventListener('load', () => resolve(undefined), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = SWIPER_JS;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-s4u-swiper', '');
    script.addEventListener('load', () => resolve(undefined), { once: true });
    script.addEventListener('error', () => reject(new Error('Failed to load Swiper')), { once: true });
    document.head.appendChild(script);
  });
}

/**
 * @param {HTMLElement} root
 */
function readConfig(root) {
  const jsonEl = root.querySelector('script[data-s4u-coverflow-config]');
  if (!jsonEl) return null;

  try {
    return JSON.parse(jsonEl.textContent || '{}');
  } catch {
    return null;
  }
}

/**
 * @param {HTMLElement} root
 */
function destroy(root) {
  const sectionId = root.getAttribute('data-section-id');
  if (!sectionId) return;

  const instance = instances.get(sectionId);
  if (instance && typeof instance.destroy === 'function') {
    try {
      instance.destroy(true, true);
    } catch {
      // ignore
    }
  }
  instances.delete(sectionId);
}

/**
 * @param {HTMLElement} root
 */
async function initOne(root) {
  const sectionId = root.getAttribute('data-section-id');
  if (!sectionId) return;

  destroy(root);

  const swiperEl = root.querySelector('.s4u-coverflow__swiper');
  if (!(swiperEl instanceof HTMLElement)) return;

  const prevEl = root.querySelector('.s4u-coverflow__nav--prev');
  const nextEl = root.querySelector('.s4u-coverflow__nav--next');

  const config = readConfig(root) ?? {};

  await ensureSwiper();

  // Remove preview layout once Swiper is active
  swiperEl.classList.remove('s4u-coverflow__swiper--preview');

  const slidesPerViewMobile = parseFloat(config.slidesPerViewMobile ?? '1.3');
  const slidesPerViewTablet = parseFloat(config.slidesPerViewTablet ?? '2');
  const slidesPerViewDesktop = parseFloat(config.slidesPerViewDesktop ?? '3');

  /** @type {any} */
  const options = {
    effect: 'coverflow',
    centeredSlides: true,
    speed: Number(config.speed ?? 600),
    loop: Boolean(config.loop ?? true),
    slidesPerView: 'auto',
    watchSlidesProgress: true,
    navigation: {
      nextEl: nextEl || undefined,
      prevEl: prevEl || undefined,
    },
    coverflowEffect: {
      rotate: 0,
      stretch: 0,
      depth: Number(config.depth ?? 200),
      modifier: 1,
      slideShadows: false,
    },
    initialSlide: Number(config.initialSlide ?? 1),
    breakpoints: {
      320: {
        slidesPerView: slidesPerViewMobile,
        spaceBetween: Number(config.spaceBetween ?? 20),
      },
      768: {
        slidesPerView: slidesPerViewTablet,
        spaceBetween: Number(config.spaceBetween ?? 20),
      },
      1024: {
        slidesPerView: slidesPerViewDesktop,
        spaceBetween: Number(config.spaceBetween ?? 20),
      },
    },
  };

  const shouldAutoplay = Boolean(config.autoplay) && !prefersReducedMotion();
  if (shouldAutoplay) {
    options.autoplay = {
      delay: Number(config.autoplayDelay ?? 4000),
      disableOnInteraction: false,
    };
  }

  const instance = new (/** @type {any} */ (window)).Swiper(swiperEl, options);
  instances.set(sectionId, instance);
}

/**
 * @param {ParentNode} scope
 */
function initAll(scope = document) {
  const roots = scope.querySelectorAll('[data-s4u-coverflow]');
  roots.forEach((el) => {
    if (el instanceof HTMLElement) {
      initOne(el);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => initAll(document));

// Theme editor support
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof HTMLElement) {
      initAll(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof HTMLElement) {
      const root = event.target.querySelector('[data-s4u-coverflow]');
      if (root instanceof HTMLElement) destroy(root);
    }
  });
}
